import React, { useEffect, useState } from 'react';
import { database } from '../firebaseConfig';
import { ref, get, set, push } from 'firebase/database';
import { clearProfileStatus } from '../services/profileStatus';
import PhoneInput from './PhoneInput';

// Lets an agent pick up to three insurance providers to recommend to their buyers.
// Choices come from the partner list, or from the agent's own providers (ids start with "mine:").
// Own providers are saved to users/{uid}/customInsurance. If the agent asks, they are also sent
// to partnerRequests so Kelly can approve them for the shared partner list.
const EMPTY = { companyName: '', firstName: '', lastName: '', phone: '', email: '' };
const ADD_NEW = '__add_new__';

// The list saved to users/{uid}/insuranceRecommendations: chosen ids, no blanks or repeats.
export const picksToSave = (list) => [...new Set((list || []).filter((v) => v && v !== ADD_NEW))];

export default function InsuranceRecommendationsSection({ uid, displayName, onSaved }) {
  const [partners, setPartners] = useState([]);
  const [mine, setMine] = useState([]);
  const [picks, setPicks] = useState(['', '', '']);
  const [form, setForm] = useState(EMPTY);
  const [sendForApproval, setSendForApproval] = useState(true);
  const [addingFor, setAddingFor] = useState(null);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!uid) return;
    (async () => {
      try {
        const [partnerSnap, pickSnap, mineSnap] = await Promise.all([
          get(ref(database, 'partners')),
          get(ref(database, `users/${uid}/insuranceRecommendations`)),
          get(ref(database, `users/${uid}/customInsurance`)),
        ]);
        const all = partnerSnap.val() || {};
        const insurance = Object.entries(all)
          .map(([id, p]) => ({ id, ...p }))
          .filter((p) => p.partnerType === 'Insurance')
          .sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));
        setPartners(insurance);
        const myVal = mineSnap.val() || {};
        setMine(Object.entries(myVal).map(([key, p]) => ({ id: `mine:${key}`, ...p })));
        const saved = Array.isArray(pickSnap.val()) ? pickSnap.val() : [];
        setPicks([saved[0] || '', saved[1] || '', saved[2] || '']);
      } catch (err) {
        console.error(err);
        setError('Could not load insurance providers.');
      }
    })();
  }, [uid]);

  const labelFor = (p) => `${p.companyName} (${p.firstName} ${p.lastName})`;
  const allOptions = [
    ...partners.map((p) => ({ value: p.id, label: labelFor(p) })),
    ...mine.map((p) => ({ value: p.id, label: `${labelFor(p)} - my provider` })),
  ];
  // A saved pick whose provider is no longer on the list still shows (instead of looking like None).
  picks.filter((v) => v && !allOptions.some((o) => o.value === v)).forEach((v) => allOptions.push({ value: v, label: 'Saved provider (no longer on the partner list)' }));

  const choose = (i, value) => {
    if (value === ADD_NEW) {
      setAddingFor(i);
      setForm(EMPTY);
      return;
    }
    const next = [...picks];
    next[i] = value;
    setPicks(next);
    // Save each pick right away, so choices never revert to None if the agent leaves the page.
    persist(next);
  };

  const persist = async (list) => {
    setError(null);
    setMsg(null);
    try {
      const chosen = picksToSave(list);
      await set(ref(database, `users/${uid}/insuranceRecommendations`), chosen.length ? chosen : null);
      if (onSaved) onSaved(chosen);
      clearProfileStatus(uid);
      setMsg(chosen.length >= 2 ? 'Saved.' : 'Saved. Choose at least two providers (three is recommended).');
    } catch (err) {
      console.error(err);
      setError('Could not save your insurance recommendations.');
    }
  };

  const saveOwnProvider = async () => {
    setError(null);
    setMsg(null);
    const required = ['companyName', 'firstName', 'lastName'];
    if (required.some((k) => !form[k].trim()) || (!form.phone.trim() && !form.email.trim())) {
      setError('Please enter the company, first and last name, and a phone or email.');
      return;
    }
    setSaving(true);
    try {
      const entry = { ...form, createdAt: new Date().toISOString() };
      const pushed = await push(ref(database, `users/${uid}/customInsurance`), entry);
      const id = `mine:${pushed.key}`;
      setMine([...mine, { id, ...entry }]);
      if (sendForApproval) {
        await push(ref(database, 'partnerRequests'), {
          ...entry,
          partnerType: 'Insurance',
          requestedBy: uid,
          requestedByName: displayName || '',
          status: 'pending',
        });
      }
      if (addingFor !== null) {
        const next = [...picks];
        next[addingFor] = id;
        setPicks(next);
        await persist(next);
      }
      setAddingFor(null);
      setForm(EMPTY);
      setMsg(sendForApproval ? 'Added. Kelly has been sent this provider to approve for the partner list.' : 'Added for your own recommendations.');
    } catch (err) {
      console.error(err);
      setError('Could not save that provider.');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setMsg(null);
    try {
      const chosen = picksToSave(picks);
      if (chosen.length < 2) {
        setError('Please choose at least two insurance providers. Three is recommended.');
        setSaving(false);
        return;
      }
      await set(ref(database, `users/${uid}/insuranceRecommendations`), chosen);
      if (onSaved) onSaved(chosen);
      clearProfileStatus(uid);
      setMsg('Insurance recommendations saved.');
    } catch (err) {
      console.error(err);
      setError('Could not save your insurance recommendations.');
    } finally {
      setSaving(false);
    }
  };

  const field = { width: '100%', boxSizing: 'border-box', padding: 8, border: '1px solid #d8c9a8', borderRadius: 6, fontSize: 14 };

  return (
    <div style={{ marginTop: 24 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 6px' }}>Insurance recommendations</h2>
      <p style={{ fontSize: 14, color: '#7f7f7f', margin: '0 0 12px' }}>
        Choose at least two insurance providers to recommend to your buyers, and three if you can. Pick from the partner list, or add your own provider.
      </p>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#7f7f7f', marginBottom: 4 }}>Recommendation {i + 1}</label>
          <select value={picks[i]} onChange={(e) => choose(i, e.target.value)} style={field}>
            <option value="">None</option>
            {allOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
            <option value={ADD_NEW}>+ Add my own provider</option>
          </select>
          {addingFor === i && (
            <div style={{ marginTop: 8, padding: 12, border: '1px solid #d8c9a8', borderRadius: 8, background: '#FFFFFF' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <input style={{ ...field, gridColumn: '1 / -1' }} placeholder="Company name" value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} />
                <input style={field} placeholder="First name" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
                <input style={field} placeholder="Last name" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
                <PhoneInput style={field} placeholder="(239) 555-1234" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <input style={field} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginTop: 10 }}>
                <input type="checkbox" checked={sendForApproval} onChange={(e) => setSendForApproval(e.target.checked)} />
                Send to Kelly to approve for the partner list
              </label>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button type="button" onClick={saveOwnProvider} disabled={saving} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#D4AF37', color: '#000', fontWeight: 600, cursor: 'pointer' }}>
                  {saving ? 'Saving...' : 'Add provider'}
                </button>
                <button type="button" onClick={() => setAddingFor(null)} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #d8c9a8', background: '#FFFFFF', color: '#5B6270', cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      ))}
      {error && <p style={{ color: '#b00020', fontSize: 14 }}>{error}</p>}
      {msg && <p style={{ color: '#2e7d32', fontSize: 14 }}>{msg}</p>}
      <button type="button" onClick={save} disabled={saving} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#D4AF37', color: '#000', fontWeight: 600, cursor: 'pointer' }}>
        {saving ? 'Saving...' : 'Save recommendations'}
      </button>
    </div>
  );
}
